/**
 * Org Management page — Members, Invitations, and Applications tabs.
 * Accessible to org owners and admins only.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { config } from '../config';

// ─── Types ───────────────────────────────────────────────────────────────────

interface OrgMember {
  id: string;
  email: string;
  name: { first?: string; last?: string } | null;
  role: 'owner' | 'admin' | 'member';
}

interface Invitation {
  id: string;
  email: string;
  target_role: string;
  status: string;
  created_at: string;
  expires_at: string;
}

interface Application {
  id: string;
  name: string;
  description: string | null;
  client_id: string;
  status: string;
  rate_limit_tier: string;
  allowed_services: string[];
  created_at: string;
}

// ─── Role helpers ─────────────────────────────────────────────────────────────

const ROLE_BADGE: Record<string, string> = {
  owner: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  admin: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  member: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

const INVITABLE_ROLES = ['employee', 'developer', 'merchant', 'member'];

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function fullName(member: OrgMember) {
  const n = member.name;
  if (!n) return member.email;
  const parts = [n.first, n.last].filter(Boolean);
  return parts.length ? parts.join(' ') : member.email;
}

// ─── Members tab ─────────────────────────────────────────────────────────────

function MembersTab({ orgId, callerRole }: { orgId: string; callerRole: string }) {
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${config.gatewayUrl}/v1/organizations/${orgId}/members`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setMembers(data.members || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [orgId]);

  const remove = async (memberId: string) => {
    if (!confirm('Remove this member from the organization?')) return;
    setRemoving(memberId);
    try {
      const res = await fetch(`${config.gatewayUrl}/v1/organizations/${orgId}/members/${memberId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok && res.status !== 204) throw new Error(await res.text());
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to remove member');
    } finally {
      setRemoving(null);
    }
  };

  if (loading) return <LoadingSpinner text="Loading members..." />;
  if (error) return <p className="text-red-500 text-sm">{error}</p>;

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead>
            <tr className="text-left text-xs font-medium text-content-secondary uppercase tracking-wider">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {members.map((m) => {
              const canRemove =
                callerRole === 'owner'
                  ? m.role !== 'owner'
                  : callerRole === 'admin' && m.role === 'member';
              return (
                <tr key={m.id}>
                  <td className="px-4 py-3 text-sm font-medium text-content-primary">{fullName(m)}</td>
                  <td className="px-4 py-3 text-sm text-content-secondary">{m.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${ROLE_BADGE[m.role] ?? ROLE_BADGE.member}`}>
                      {m.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canRemove && (
                      <button
                        onClick={() => remove(m.id)}
                        disabled={removing === m.id}
                        className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                      >
                        {removing === m.id ? 'Removing…' : 'Remove'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {members.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-sm text-content-tertiary">
                  No members found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Invitations tab ──────────────────────────────────────────────────────────

function InvitationsTab({ orgId }: { orgId: string }) {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Send form
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState<string | null>(null);

  const [revoking, setRevoking] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${config.gatewayUrl}/v1/invitations?org_id=${orgId}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(await res.text());
      setInvitations(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [orgId]);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setSendError(null);
    setSendSuccess(null);
    try {
      const res = await fetch(`${config.gatewayUrl}/v1/invitations`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, org_id: orgId, target_role: role }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to send invitation');
      }
      setSendSuccess(`Invitation sent to ${email}`);
      setEmail('');
      setRole('member');
      load();
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  const revoke = async (id: string) => {
    if (!confirm('Revoke this invitation?')) return;
    setRevoking(id);
    try {
      const res = await fetch(`${config.gatewayUrl}/v1/invitations/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok && res.status !== 204) throw new Error(await res.text());
      setInvitations((prev) => prev.filter((inv) => inv.id !== id));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to revoke invitation');
    } finally {
      setRevoking(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Send invitation form */}
      <div className="bg-surface-secondary rounded-lg p-4">
        <h3 className="text-sm font-semibold text-content-primary mb-3">Send Invitation</h3>
        <form onSubmit={send} className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="flex-1 px-3 py-2 border border-border rounded-md bg-surface-primary text-sm text-content-primary focus:outline-none focus:ring-2 focus:ring-orbital-500"
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="px-3 py-2 border border-border rounded-md bg-surface-primary text-sm text-content-primary focus:outline-none focus:ring-2 focus:ring-orbital-500"
          >
            {INVITABLE_ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <button
            type="submit"
            disabled={sending}
            className="px-4 py-2 bg-orbital-600 text-white text-sm font-medium rounded-md hover:bg-orbital-700 disabled:opacity-50"
          >
            {sending ? 'Sending…' : 'Send Invite'}
          </button>
        </form>
        {sendError && <p className="mt-2 text-sm text-red-500">{sendError}</p>}
        {sendSuccess && <p className="mt-2 text-sm text-green-600 dark:text-green-400">{sendSuccess}</p>}
      </div>

      {/* Pending invitations list */}
      <div>
        <h3 className="text-sm font-semibold text-content-primary mb-3">Pending Invitations</h3>
        {loading ? (
          <LoadingSpinner text="Loading invitations..." />
        ) : error ? (
          <p className="text-sm text-red-500">{error}</p>
        ) : invitations.length === 0 ? (
          <p className="text-sm text-content-tertiary">No pending invitations.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead>
                <tr className="text-left text-xs font-medium text-content-secondary uppercase tracking-wider">
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Expires</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {invitations.map((inv) => (
                  <tr key={inv.id}>
                    <td className="px-4 py-3 text-sm text-content-primary">{inv.email}</td>
                    <td className="px-4 py-3 text-sm text-content-secondary">{inv.target_role}</td>
                    <td className="px-4 py-3 text-sm text-content-secondary">{formatDate(inv.expires_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => revoke(inv.id)}
                        disabled={revoking === inv.id}
                        className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 disabled:opacity-50"
                      >
                        {revoking === inv.id ? 'Revoking…' : 'Revoke'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Applications tab ─────────────────────────────────────────────────────────

function ApplicationsTab({ orgId }: { orgId: string }) {
  const [apps, setApps] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Register form
  const [showForm, setShowForm] = useState(false);
  const [appName, setAppName] = useState('');
  const [appDesc, setAppDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newCredentials, setNewCredentials] = useState<{ client_id: string; client_secret: string } | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${config.gatewayUrl}/v1/organizations/${orgId}/applications`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setApps(data.applications || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [orgId]);

  const register = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    try {
      const res = await fetch(`${config.gatewayUrl}/v1/organizations/${orgId}/applications`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: appName, description: appDesc || undefined }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to register application');
      }
      const data = await res.json();
      setNewCredentials({ client_id: data.application.client_id, client_secret: data.client_secret });
      setAppName('');
      setAppDesc('');
      setShowForm(false);
      load();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to register application');
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <LoadingSpinner text="Loading applications..." />;
  if (error) return <p className="text-red-500 text-sm">{error}</p>;

  return (
    <div className="space-y-4">
      {/* New credentials reveal */}
      {newCredentials && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <p className="text-sm font-semibold text-green-800 dark:text-green-300 mb-2">
            Application registered — save these credentials now, the secret won't be shown again.
          </p>
          <div className="space-y-1 font-mono text-xs text-green-900 dark:text-green-200">
            <div><span className="font-semibold">Client ID:</span> {newCredentials.client_id}</div>
            <div><span className="font-semibold">Client Secret:</span> {newCredentials.client_secret}</div>
          </div>
          <button
            onClick={() => setNewCredentials(null)}
            className="mt-3 text-xs text-green-700 dark:text-green-400 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h3 className="text-sm font-semibold text-content-primary">OAuth2 Applications</h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-3 py-1.5 bg-orbital-600 text-white text-xs font-medium rounded-md hover:bg-orbital-700"
        >
          + Register App
        </button>
      </div>

      {/* Register form */}
      {showForm && (
        <form onSubmit={register} className="bg-surface-secondary rounded-lg p-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-content-secondary mb-1">App Name</label>
            <input
              type="text"
              value={appName}
              onChange={(e) => setAppName(e.target.value)}
              required
              className="w-full px-3 py-2 border border-border rounded-md bg-surface-primary text-sm text-content-primary focus:outline-none focus:ring-2 focus:ring-orbital-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-content-secondary mb-1">Description (optional)</label>
            <input
              type="text"
              value={appDesc}
              onChange={(e) => setAppDesc(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-surface-primary text-sm text-content-primary focus:outline-none focus:ring-2 focus:ring-orbital-500"
            />
          </div>
          {createError && <p className="text-sm text-red-500">{createError}</p>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={creating}
              className="px-4 py-2 bg-orbital-600 text-white text-sm font-medium rounded-md hover:bg-orbital-700 disabled:opacity-50"
            >
              {creating ? 'Registering…' : 'Register'}
            </button>
            <button
              type="button"
              onClick={() => { setShowForm(false); setCreateError(null); }}
              className="px-4 py-2 border border-border text-sm rounded-md hover:bg-surface-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Apps list */}
      {apps.length === 0 ? (
        <p className="text-sm text-content-tertiary">No applications registered.</p>
      ) : (
        <div className="space-y-3">
          {apps.map((app) => (
            <div key={app.id} className="border border-border rounded-lg p-4 bg-surface-secondary">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm font-medium text-content-primary">{app.name}</p>
                  {app.description && (
                    <p className="text-xs text-content-tertiary mt-0.5">{app.description}</p>
                  )}
                  <p className="text-xs text-content-secondary mt-1 font-mono">{app.client_id}</p>
                </div>
                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                  app.status === 'active'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  {app.status}
                </span>
              </div>
              <div className="mt-2 flex gap-4 text-xs text-content-tertiary">
                <span>Tier: {app.rate_limit_tier}</span>
                {app.allowed_services.length > 0 && (
                  <span>Services: {app.allowed_services.join(', ')}</span>
                )}
                <span>Created: {formatDate(app.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

type Tab = 'members' | 'invitations' | 'applications';

export default function OrgManage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, currentOrganization } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('members');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading..." />
      </div>
    );
  }

  if (!currentOrganization) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <p className="text-content-secondary">No organization selected.</p>
      </div>
    );
  }

  const callerRole = currentOrganization.role;
  if (callerRole !== 'owner' && callerRole !== 'admin') {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <p className="text-content-secondary">You need owner or admin access to manage this organization.</p>
      </div>
    );
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'members', label: 'Members' },
    { id: 'invitations', label: 'Invitations' },
    { id: 'applications', label: 'Applications' },
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-content-primary">Manage Organization</h1>
        <p className="text-sm text-content-secondary mt-1">{currentOrganization.name}</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-border mb-6">
        <nav className="-mb-px flex space-x-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-orbital-600 text-orbital-600 dark:border-orbital-400 dark:text-orbital-400'
                  : 'border-transparent text-content-secondary hover:text-content-primary hover:border-border'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div className="bg-surface-primary rounded-lg border border-border p-6">
        {activeTab === 'members' && (
          <MembersTab orgId={currentOrganization.id} callerRole={callerRole} />
        )}
        {activeTab === 'invitations' && (
          <InvitationsTab orgId={currentOrganization.id} />
        )}
        {activeTab === 'applications' && (
          <ApplicationsTab orgId={currentOrganization.id} />
        )}
      </div>
    </div>
  );
}
